import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { agentsAPI, knowledgeAPI, formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2, Save, Search, FolderTree, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const COLLECTION_OPTIONS = ['code', 'docs', 'tickets', 'artifacts'];

function skillEnabled(agentSkills, skillId) {
  return (agentSkills || []).some((s) => s.skill_id === skillId && s.enabled);
}

export default function AgentRuntimeTab({
  tenantId,
  capsuleId,
  agent,
  agentSkills,
  onAgentUpdated,
}) {
  const [savingInv, setSavingInv] = useState(false);
  const [savingKb, setSavingKb] = useState(false);
  const [invMode, setInvMode] = useState('off');
  const [invRounds, setInvRounds] = useState(6);
  const [invVerify, setInvVerify] = useState(false);
  const [kbCfg, setKbCfg] = useState({});
  const [kbLoading, setKbLoading] = useState(true);
  const [repoScope, setRepoScope] = useState([]);
  const [capsuleRepos, setCapsuleRepos] = useState([]);
  const [savingRepoScope, setSavingRepoScope] = useState(false);
  const [sourceScope, setSourceScope] = useState([]);
  const [capsuleMounts, setCapsuleMounts] = useState([]);
  const [savingSourceScope, setSavingSourceScope] = useState(false);

  const hasKbSkill = skillEnabled(agentSkills, 'knowledge-search');
  const hasArtifactSkill = skillEnabled(agentSkills, 'artifact-generator');
  const invEffective = invMode === 'iterative' && hasKbSkill;

  useEffect(() => {
    const cfg = agent?.investigation_config || {};
    setInvMode(cfg.mode === 'iterative' ? 'iterative' : 'off');
    setInvRounds(cfg.max_investigation_rounds ?? 6);
    setInvVerify(!!cfg.verification_pass);
  }, [agent?.agent_id, agent?.investigation_config]);

  useEffect(() => {
    if (!capsuleId || !agent?.agent_id) return;
    let cancelled = false;
    (async () => {
      setKbLoading(true);
      try {
        const res = await knowledgeAPI.getAgentConfig(capsuleId, agent.agent_id);
        if (!cancelled) setKbCfg(res.data?.config || {});
      } catch {
        if (!cancelled) setKbCfg({});
      } finally {
        if (!cancelled) setKbLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [capsuleId, agent?.agent_id]);

  useEffect(() => {
    if (!capsuleId || !agent?.agent_id) return;
    (async () => {
      try {
        const res = await knowledgeAPI.getAgentRepoScope(capsuleId, agent.agent_id);
        setRepoScope(res.data?.kb_repo_scope || []);
        setCapsuleRepos((res.data?.capsule_repo_access || []).map((r) => r.repo_id));
      } catch {
        setRepoScope([]);
        setCapsuleRepos([]);
      }
    })();
  }, [capsuleId, agent?.agent_id]);

  useEffect(() => {
    if (!capsuleId || !agent?.agent_id) return;
    (async () => {
      try {
        const res = await knowledgeAPI.getAgentSourceScope(capsuleId, agent.agent_id);
        setSourceScope(res.data?.kb_source_scope || []);
        setCapsuleMounts((res.data?.capsule_source_access || []).map((m) => m.mount_id));
      } catch {
        setSourceScope([]);
        setCapsuleMounts([]);
      }
    })();
  }, [capsuleId, agent?.agent_id]);

  const handleSaveRepoScope = async () => {
    if (!agent) return;
    setSavingRepoScope(true);
    try {
      await knowledgeAPI.updateAgentRepoScope(capsuleId, agent.agent_id, { kb_repo_scope: repoScope });
      toast.success('Scope de repos guardado');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingRepoScope(false);
    }
  };

  const toggleRepoScope = (repoId) => {
    setRepoScope((prev) => (
      prev.includes(repoId) ? prev.filter((r) => r !== repoId) : [...prev, repoId]
    ));
  };

  const handleSaveSourceScope = async () => {
    if (!agent) return;
    setSavingSourceScope(true);
    try {
      await knowledgeAPI.updateAgentSourceScope(capsuleId, agent.agent_id, { kb_source_scope: sourceScope });
      toast.success('Scope de mounts guardado');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingSourceScope(false);
    }
  };

  const toggleSourceScope = (mountId) => {
    setSourceScope((prev) => (
      prev.includes(mountId) ? prev.filter((m) => m !== mountId) : [...prev, mountId]
    ));
  };

  const handleSaveInvestigation = async () => {
    if (!agent) return;
    setSavingInv(true);
    try {
      const res = await agentsAPI.update(tenantId, capsuleId, agent.agent_id, {
        investigation_config: {
          mode: invMode,
          max_investigation_rounds: Math.min(8, Math.max(1, Number(invRounds) || 3)),
          verification_pass: invVerify,
        },
      });
      toast.success('Investigación KB guardada');
      onAgentUpdated?.(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingInv(false);
    }
  };

  const handleSaveKbOverride = async () => {
    if (!agent) return;
    setSavingKb(true);
    try {
      const payload = { ...kbCfg };
      if (payload.max_results === '' || payload.max_results == null) delete payload.max_results;
      if (payload.min_score === '' || payload.min_score == null) delete payload.min_score;
      if (!payload.kb_answer_model_ref) delete payload.kb_answer_model_ref;
      // Stubs no editables desde UI (aún no afectan retrieval real)
      delete payload.context_budget_tokens;
      delete payload.retrieval_mode;
      const res = await knowledgeAPI.updateAgentConfig(capsuleId, agent.agent_id, payload);
      setKbCfg(res.data?.config || {});
      toast.success('Override KB guardado');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingKb(false);
    }
  };

  const toggleCollection = (name) => {
    const current = kbCfg.collection_types || COLLECTION_OPTIONS;
    const next = current.includes(name)
      ? current.filter((c) => c !== name)
      : [...current, name];
    setKbCfg((p) => ({ ...p, collection_types: next.length ? next : ['docs'] }));
  };

  const kbLink = `/knowledge?tab=config&capsule=${encodeURIComponent(capsuleId)}&agent=${encodeURIComponent(agent?.agent_id || '')}`;

  return (
    <div className="space-y-4" data-testid="agent-runtime-tab">
      <Card className="border border-brand-border rounded-sm">
        <CardHeader className="pb-2 border-b border-brand-border">
          <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] flex items-center gap-2">
            <Search className="w-3.5 h-3.5" />
            Investigación KB iterativa
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-3 text-xs">
          <p className="text-zinc-600 leading-relaxed">
            Con modo <strong>iterative</strong>, el agente opera como harness (bucle interno en el mismo turno):{' '}
            <code className="bg-zinc-100 px-1 rounded">kb_list_dir</code>,{' '}
            <code className="bg-zinc-100 px-1 rounded">kb_glob</code>,{' '}
            <code className="bg-zinc-100 px-1 rounded">kb_grep</code>,{' '}
            <code className="bg-zinc-100 px-1 rounded">kb_search</code>,{' '}
            <code className="bg-zinc-100 px-1 rounded">kb_read_source</code>,{' '}
            <code className="bg-zinc-100 px-1 rounded">kb_expand_graph</code>.
            Requiere skill <code className="bg-zinc-100 px-1 rounded">knowledge-search</code>.
            Playbook sugerido: <code className="bg-zinc-100 px-1 rounded">soporte-kb-investigation-RULES-playbook.md</code>
            (fusionar en RULES del agente). Las rondas KB deben caber bajo el presupuesto HTTP de plataforma
            (<code className="bg-zinc-100 px-1 rounded">http_turn_budget_sec</code>, default 600s); si no, nginx/cliente cortan con 504
            antes del Turn Health. Al agotar rondas el harness informa el límite (no inventar cierre).
          </p>
          <div className="flex flex-wrap gap-2">
            <Badge variant={hasKbSkill ? 'default' : 'secondary'} className="text-[10px]">
              knowledge-search: {hasKbSkill ? 'activa' : 'inactiva'}
            </Badge>
            <Badge variant={invEffective ? 'default' : 'secondary'} className="text-[10px]">
              runtime: {invEffective ? 'investigación ON' : 'single-shot RAG'}
            </Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">Modo</Label>
              <select
                data-testid="investigation-mode-select"
                className="mt-1 h-8 w-full text-xs border border-brand-border rounded-sm px-2"
                value={invMode}
                onChange={(e) => setInvMode(e.target.value)}
              >
                <option value="off">off (RAG inicial únicamente)</option>
                <option value="iterative">iterative (bucle kb_*)</option>
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">Máx. rondas KB (1–8)</Label>
              <Input
                type="number"
                min={1}
                max={8}
                data-testid="investigation-rounds-input"
                className="mt-1 h-8 text-xs rounded-sm"
                value={invRounds}
                onChange={(e) => setInvRounds(e.target.value)}
                disabled={invMode !== 'iterative'}
              />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-xs text-zinc-700">
                <Switch
                  checked={invVerify}
                  onCheckedChange={setInvVerify}
                  disabled={invMode !== 'iterative'}
                  data-testid="investigation-verify-switch"
                />
                verification_pass (reservado)
              </label>
            </div>
          </div>
          {invMode === 'iterative' && !hasKbSkill && (
            <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5">
              Activá la habilidad <strong>knowledge-search</strong> en la pestaña «Qué puede hacer» para que el modo iterative surta efecto.
            </p>
          )}
          <Button
            size="sm"
            className="h-8 text-xs bg-brand-primary text-white"
            onClick={handleSaveInvestigation}
            disabled={savingInv}
            data-testid="save-investigation-btn"
          >
            {savingInv ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
            Guardar investigación
          </Button>
        </CardContent>
      </Card>

      <Card className="border border-brand-border rounded-sm">
        <CardHeader className="pb-2 border-b border-brand-border">
          <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] flex items-center gap-2">
            <FolderTree className="w-3.5 h-3.5" />
            Workspace de ticket
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-2 text-xs text-zinc-600">
          <p>
            Para crear/editar artefactos Markdown en tickets, el agente necesita la skill{' '}
            <code className="bg-zinc-100 px-1 rounded">artifact-generator</code> y un ticket vinculado.
            Usa kernel tools <code className="bg-zinc-100 px-1 rounded">ws_write</code> / <code className="bg-zinc-100 px-1 rounded">ws_append</code>.
            En Asistente de ticket: si el operador pide generar/guardar un documento, el protocolo RULES
            (playbook ticket) indica escribir con <code className="bg-zinc-100 px-1 rounded">ws_write</code> en el mismo turno,
            no solo anunciar &quot;voy a investigar&quot;. Autopsia: <code className="bg-zinc-100 px-1 rounded">ticket-chat-{'{ticket_id}'}</code>.
          </p>
          <Badge variant={hasArtifactSkill ? 'default' : 'secondary'} className="text-[10px]">
            artifact-generator: {hasArtifactSkill ? 'activa' : 'inactiva'}
          </Badge>
          {!hasArtifactSkill && (
            <p className="text-zinc-500">Habilitala en la pestaña «Qué puede hacer» si este agente debe generar documentos en tickets.</p>
          )}
        </CardContent>
      </Card>

      <Card className="border border-brand-border rounded-sm">
        <CardHeader className="pb-2 border-b border-brand-border flex-row items-center justify-between">
          <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em]">
            Override KB por agente
          </CardTitle>
          <Link
            to={kbLink}
            className="text-[10px] text-brand-primary hover:underline flex items-center gap-1"
            data-testid="open-kb-config-link"
          >
            Abrir en Knowledge <ExternalLink className="w-3 h-3" />
          </Link>
        </CardHeader>
        <CardContent className="pt-4 space-y-3 text-xs">
          {kbLoading ? (
            <SkeletonLine />
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500">max_results</Label>
                  <Input
                    type="number"
                    className="mt-1 h-8 text-xs rounded-sm"
                    placeholder="default tenant"
                    value={kbCfg.max_results ?? ''}
                    onChange={(e) => setKbCfg((p) => ({ ...p, max_results: e.target.value === '' ? null : Number(e.target.value) }))}
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500">min_score</Label>
                  <Input
                    type="number"
                    step="0.01"
                    className="mt-1 h-8 text-xs rounded-sm"
                    placeholder="default tenant"
                    value={kbCfg.min_score ?? ''}
                    onChange={(e) => setKbCfg((p) => ({ ...p, min_score: e.target.value === '' ? null : Number(e.target.value) }))}
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500">kb_answer_model_ref</Label>
                  <Input
                    className="mt-1 h-8 text-xs rounded-sm font-mono"
                    placeholder="opcional"
                    value={kbCfg.kb_answer_model_ref ?? ''}
                    onChange={(e) => setKbCfg((p) => ({ ...p, kb_answer_model_ref: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!kbCfg.use_graph_enrichment}
                    onChange={(e) => setKbCfg((p) => ({ ...p, use_graph_enrichment: e.target.checked }))}
                  />
                  use_graph_enrichment
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={kbCfg.rerank !== false}
                    onChange={(e) => setKbCfg((p) => ({ ...p, rerank: e.target.checked }))}
                  />
                  rerank
                </label>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">Colecciones</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {COLLECTION_OPTIONS.map((c) => (
                    <label key={c} className="flex items-center gap-1 text-[11px]">
                      <input
                        type="checkbox"
                        checked={(kbCfg.collection_types || COLLECTION_OPTIONS).includes(c)}
                        onChange={() => toggleCollection(c)}
                      />
                      {c}
                    </label>
                  ))}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={handleSaveKbOverride}
                disabled={savingKb}
                data-testid="save-kb-override-btn"
              >
                {savingKb ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                Guardar override KB
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="border border-brand-border rounded-sm">
        <CardHeader className="pb-2 border-b border-brand-border">
          <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] flex items-center gap-2">
            <FolderTree className="w-3.5 h-3.5" />
            Repos Git (scope agente)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-3 text-xs">
          <p className="text-zinc-600">
            Elige que repos de la capsula puede usar este agente. Sin ninguno marcado explicitamente, hereda todos los repos autorizados en Config Capsula → Conocimiento.
          </p>
          {capsuleRepos.length === 0 ? (
            <p className="text-zinc-400">Sin repos asignados a la capsula. Configuralos en Config Capsula → Conocimiento.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {capsuleRepos.map((rid) => (
                <label key={rid} className="flex items-center gap-1 border rounded-sm px-2 py-1">
                  <input
                    type="checkbox"
                    checked={repoScope.length === 0 || repoScope.includes(rid)}
                    onChange={() => toggleRepoScope(rid)}
                  />
                  {rid}
                </label>
              ))}
            </div>
          )}
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleSaveRepoScope} disabled={savingRepoScope}>
            {savingRepoScope ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
            Guardar repos del agente
          </Button>
        </CardContent>
      </Card>

      <Card className="border border-brand-border rounded-sm">
        <CardHeader className="pb-2 border-b border-brand-border">
          <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] flex items-center gap-2">
            <FolderTree className="w-3.5 h-3.5" />
            Mounts locales (scope agente)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-3 text-xs">
          <p className="text-zinc-600">
            Elige que mounts locales de la capsula puede usar este agente. Sin ninguno marcado explicitamente, hereda todos los mounts autorizados en Config Capsula → Conocimiento.
          </p>
          {capsuleMounts.length === 0 ? (
            <p className="text-zinc-400">Sin mounts asignados a la capsula. Configuralos en Config Capsula → Conocimiento.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {capsuleMounts.map((mid) => (
                <label key={mid} className="flex items-center gap-1 border rounded-sm px-2 py-1">
                  <input
                    type="checkbox"
                    checked={sourceScope.length === 0 || sourceScope.includes(mid)}
                    onChange={() => toggleSourceScope(mid)}
                  />
                  {mid}
                </label>
              ))}
            </div>
          )}
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleSaveSourceScope} disabled={savingSourceScope}>
            {savingSourceScope ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
            Guardar mounts del agente
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function SkeletonLine() {
  return <div className="h-20 bg-zinc-100 animate-pulse rounded-sm" />;
}
